"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearTheme = exports.clearThemesOptions = void 0;
const material_1 = require("@mui/material");
const colors_1 = require("../tokens/colors");
const shadows_1 = require("../tokens/shadows");
const shape_1 = require("../tokens/shape");
const typography_1 = require("../tokens/typography");
const base_theme_1 = require("./base-theme");
// opacity percentage to hexcode conversion - https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4
const baseThemeWithPalette = (0, material_1.createTheme)(base_theme_1.baseTheme, {
    palette: {
        action: {
            active: '#6b778c',
            hover: colors_1.colors.primary['Background'],
            hoverOpacity: 0.04,
            selected: colors_1.colors.black,
            selectedOpacity: 0.08,
            disabled: colors_1.colors.text['Disabled'],
            disabledBackground: colors_1.colors.other['DisabledBackground'],
        },
        background: {
            default: '#f4f5f7',
        },
        mode: 'light',
        primary: {
            contrastText: colors_1.colors.primary['ContrastText'],
            main: colors_1.colors.primary['Main'],
            dark: colors_1.colors.primary['Dark'],
            light: colors_1.colors.primary['Light'],
        },
        secondary: {
            contrastText: colors_1.colors.secondary['ContrastText'],
            main: colors_1.colors.secondary['Main'],
            dark: colors_1.colors.secondary['Dark'],
            light: colors_1.colors.secondary['Light'],
        },
        info: {
            contrastText: colors_1.colors.info['ContrastText'],
            main: colors_1.colors.info['Main'],
            dark: colors_1.colors.info['Dark'],
            light: colors_1.colors.info['Light'],
        },
        error: {
            contrastText: colors_1.colors.error['ContrastText'],
            main: colors_1.colors.error['Main'],
            dark: colors_1.colors.error['Dark'],
            light: colors_1.colors.error['Light'],
        },
        success: {
            contrastText: colors_1.colors.success['ContrastText'],
            main: colors_1.colors.success['Main'],
            dark: colors_1.colors.success['Dark'],
            light: colors_1.colors.success['Light'],
        },
        warning: {
            contrastText: colors_1.colors.warning['ContrastText'],
            main: colors_1.colors.warning['Main'],
            dark: colors_1.colors.warning['Dark'],
            light: colors_1.colors.warning['Light'],
        },
        text: {
            primary: colors_1.colors.text['Primary'],
            secondary: colors_1.colors.text['Secondary'],
            disabled: colors_1.colors.text['Disabled'],
        },
        grey: colors_1.colors.grey,
    },
});
exports.clearThemesOptions = {
    components: {
        MuiAvatar: {
            styleOverrides: {
                fallback: {
                    height: '75%',
                    width: '75%',
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                dot: {
                    backgroundColor: baseThemeWithPalette.palette.warning.main,
                    right: `-7px`,
                    top: '2px',
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    a: {
                        color: baseThemeWithPalette.palette.text.secondary,
                        '&:hover': {
                            color: `${colors_1.colors.black}61`, // opacity 38%
                        },
                    },
                    'li:last-of-type > a': {
                        color: baseThemeWithPalette.palette.text.primary,
                    },
                },
                separator: {
                    color: baseThemeWithPalette.palette.text.primary,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    '&.MuiButton-outlinedError': {
                        ':hover': {
                            backgroundColor: colors_1.colors.error.LightBg,
                        },
                    },
                    '&.MuiButton-textError': {
                        ':hover': {
                            backgroundColor: colors_1.colors.error.LightBg,
                        },
                    },
                    '&.Mui-disabled': {
                        color: baseThemeWithPalette.palette.action.disabled,
                    },
                },
                containedPrimary: {
                    color: baseThemeWithPalette.palette.primary.contrastText,
                },
                outlinedPrimary: {
                    ':hover': {
                        backgroundColor: colors_1.colors.primary.Background,
                    },
                },
                textPrimary: {
                    ':hover': {
                        backgroundColor: colors_1.colors.primary.Background,
                    },
                },
                outlinedSecondary: {
                    ':hover': {
                        backgroundColor: colors_1.colors.secondary.Background,
                    },
                },
                textSecondary: {
                    ':hover': {
                        backgroundColor: colors_1.colors.secondary.Background,
                    },
                },
                sizeLarge: {
                    fontWeight: typography_1.typography.buttonLarge.fontWeight,
                    fontSize: typography_1.typography.buttonLarge.fontSize,
                    letterSpacing: typography_1.typography.buttonLarge.letterSpacing,
                },
                sizeMedium: {
                    fontWeight: typography_1.typography.buttonMedium.fontWeight,
                    fontSize: typography_1.typography.buttonMedium.fontSize,
                    letterSpacing: typography_1.typography.buttonMedium.letterSpacing,
                },
                sizeSmall: {
                    fontWeight: typography_1.typography.buttonSmall.fontWeight,
                    fontSize: typography_1.typography.buttonSmall.fontSize,
                    letterSpacing: typography_1.typography.buttonSmall.letterSpacing,
                },
                iconSizeLarge: {
                    height: 16,
                    width: 16,
                },
                iconSizeMedium: {
                    height: 13.33,
                    width: 13.33,
                },
                iconSizeSmall: {
                    height: 12,
                    width: 12,
                },
                startIcon: {
                    marginRight: 12,
                    marginBottom: 8,
                },
                endIcon: {
                    marginRight: 0,
                    marginLeft: 12,
                    marginBottom: 8,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    marginBottom: '0.75rem',
                    '.MuiButton-root': {
                        width: '100%',
                    },
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '0 1.5rem 1.5rem 1.5rem',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '1.5rem 1.5rem 0 1.5rem',
                    fontSize: typography_1.typography.h6.fontSize,
                    fontWeight: typography_1.typography.h6.fontWeight,
                    letterSpacing: typography_1.typography.h6.letterSpacing,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    '&.MuiChip-colorDefault': {
                        color: baseThemeWithPalette.palette.text.primary,
                    },
                    '&.MuiChip-filledDefault': {
                        backgroundColor: baseThemeWithPalette.palette.grey[300],
                    },
                    '&.MuiChip-outlined': {
                        backgroundColor: baseThemeWithPalette.palette.secondary.contrastText,
                    },
                    '&.MuiChip-outlinedDefault': {
                        border: `1px solid ${baseThemeWithPalette.palette.grey[300]}`,
                    },
                    // Autocomplete selector with chips
                    '&.MuiAutocomplete-tag': {
                        backgroundColor: baseThemeWithPalette.palette.primary.main,
                        '& > .MuiChip-label': {
                            color: baseThemeWithPalette.palette.primary.contrastText,
                        },
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    textAlign: 'center',
                    color: baseThemeWithPalette.palette.text.primary,
                    '&.standard-dialog': {
                        textAlign: 'left',
                        '.MuiIconButton-root': {
                            position: 'absolute',
                            top: '0',
                            right: '0',
                        },
                    },
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '16px 24px 24px',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    '&::placeholder': {
                        opacity: 0.86,
                        color: baseThemeWithPalette.palette.text.disabled,
                    },
                    '&.MuiInputBase-input.MuiInputBase-inputMultiline': {
                        padding: '0',
                    },
                },
                root: {
                    // Outlined autocomplete select box
                    '&.MuiAutocomplete-inputRoot.MuiOutlinedInput-root ': {
                        padding: '2px 39px 2px 9px',
                    },
                    '&.Mui-disabled': {
                        border: `1px solid ${baseThemeWithPalette.palette.grey[50]}`,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                input: {
                    padding: '0.6rem 0.8rem',
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    '.MuiAlert-root': {
                        color: baseThemeWithPalette.palette.text.primary,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    opacity: 1,
                    minWidth: '160px',
                    width: 'fit-content',
                    color: baseThemeWithPalette.palette.text.secondary,
                    fontWeight: typography_1.typography.buttonMedium.fontWeight,
                    fontSize: typography_1.typography.buttonMedium.fontSize,
                    letterSpacing: typography_1.typography.buttonMedium.letterSpacing,
                    '&.active': {
                        color: baseThemeWithPalette.palette.primary.main,
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    color: baseThemeWithPalette.palette.primary.contrastText,
                    backgroundColor: baseThemeWithPalette.palette.grey[800],
                    padding: '0.75rem',
                    fontSize: '14px',
                },
                arrow: {
                    color: baseThemeWithPalette.palette.grey[800],
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: typography_1.typography.subtitle1.fontSize,
                    fontWeight: typography_1.typography.subtitle1.fontWeight,
                    letterSpacing: typography_1.typography.subtitle1.letterSpacing,
                    color: baseThemeWithPalette.palette.text.primary,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: baseThemeWithPalette.palette.text.disabled,
                    },
                    '&.Mui-error': {
                        color: baseThemeWithPalette.palette.error.main,
                    },
                },
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    backgroundColor: colors_1.colors.white,
                    color: baseThemeWithPalette.palette.text.primary,
                },
                message: {
                    color: baseThemeWithPalette.palette.text.primary,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    ' .MuiTablePagination-selectLabel': {
                        fontSize: typography_1.typography.caption.fontSize,
                        fontWeight: typography_1.typography.caption.fontWeight,
                        letterSpacing: typography_1.typography.caption.letterSpacing,
                        color: baseThemeWithPalette.palette.text.secondary,
                    },
                    ' .MuiTablePagination-displayedRows': {
                        fontSize: typography_1.typography.caption.fontSize,
                        fontWeight: typography_1.typography.caption.fontWeight,
                        letterSpacing: typography_1.typography.caption.letterSpacing,
                    },
                    ' .MuiTablePagination-actions': {
                        color: baseThemeWithPalette.palette.text.secondary,
                    },
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    height: '56px',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: `${colors_1.colors.black}0A`, // opacity 4%
                    },
                    '&:selected': {
                        backgroundColor: `${colors_1.colors.black}14`, // opacity 8%
                    },
                },
            },
        },
    },
    shadows: shadows_1.lightShadows,
    shape: {
        borderRadius: shape_1.shape.borderRadius,
    },
};
exports.clearTheme = (0, material_1.createTheme)(exports.clearThemesOptions, baseThemeWithPalette);
//# sourceMappingURL=clear-theme.js.map